import {ref, type Ref} from 'vue';
import {PRIVATE_CAPTCHA_SITE_KEY} from '@/ts/utils/consts';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {AppError} from '@/ts/utils/errors';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';
import {sentryService} from '@/ts/services/sentry-service';

// Global types for Private Captcha
declare global {
  interface Window {
    privateCaptcha?: {
      autoWidget?: {
        solution(): string | null;
        reset(options?: {debug?: boolean; theme?: string}): void;
      };
      render(element: HTMLElement, options: {
        sitekey: string;
        theme?: string;
        lang?: string;
        displayMode?: string;
        startMode?: string;
        debug?: boolean;
        styles?: string;
      }): void;
    };
  }
}

let scriptLoaded = false;
let loadingPromise: Promise<void> | null = null;
// The <script> tag of the in-flight load. A timed-out (stalled) tag is removed
// so a retry appends a clean one; late callbacks from an abandoned tag are
// ignored by identity so they can't clobber a newer attempt's state.
let pendingScript: HTMLScriptElement | null = null;

// Shared state across all composable instances (singleton pattern)
const isWidgetReady = ref(false);
const isCaptchaLoading = ref(false);
const isCaptchaSolved = ref(false);
const captchaToken = ref<string | null>(null);

// Per-element AbortController so a re-render on the same node tears down the
// prior listeners. Today sign-up-captcha re-renders by bumping a `:key`, which
// recreates the DOM node and orphans the old listeners; this guards future
// callers that mutate the same node in place.
const widgetAbortControllers = new WeakMap<HTMLElement, AbortController>();

async function loadPrivateCaptchaScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.privatecaptcha.com/widget/js/privatecaptcha.js?render=explicit';
    script.defer = true;

    pendingScript = script;

    script.onload = (): void => {
      scriptLoaded = true;
      if (pendingScript === script) {
        pendingScript = null;
        loadingPromise = null;
      }
      resolve();
    };

    script.onerror = (error): void => {
      const logger = useLogger();
      if (pendingScript === script) {
        pendingScript = null;
        loadingPromise = null;
      }
      // Drop the failed tag so a retry appends a clean one.
      script.remove();
      logger.error('Failed to load Private Captcha script:', error)();
      const appError = new AppError('Failed to load Private Captcha script', ErrorCodesDto.CAPTCHA_ERROR);
      sentryService.captureException(appError, {captchaPhase: 'script-load', originalError: error});
      reject(appError);
    };

    document.head.appendChild(script);
  });
}

/**
 * Composable for managing Private Captcha widget with explicit rendering.
 *
 * Uses event listeners to track widget state:
 * - isWidgetReady: True when widget is initialized
 * - isCaptchaLoading: True while user is solving captcha
 * - isCaptchaSolved: True when captcha is completed
 * - captchaToken: Solution token (null until solved)
 * - resetCaptcha: Reset state (component handles widget re-render)
 * - renderWidget: Manually render widget on a DOM element with event listeners
 */
export function usePrivateCaptcha(siteKey: string | undefined = PRIVATE_CAPTCHA_SITE_KEY): {
  isWidgetReady: Ref<boolean>;
  isCaptchaLoading: Ref<boolean>;
  isCaptchaSolved: Ref<boolean>;
  captchaToken: Ref<string | null>;
  resetCaptcha(): void;
  waitForScriptLoad(): Promise<void>;
  renderWidget(element: HTMLElement, options: {
    sitekey: string;
    theme?: string;
    lang?: string;
    displayMode?: string;
    startMode?: string;
    debug?: boolean;
    styles?: string;
  }): void;
} {
  const logger = useLogger();

  // No site key = no captcha required
  if (!siteKey) {
    return {
      isWidgetReady: ref(true),
      isCaptchaLoading: ref(false),
      isCaptchaSolved: ref(true),
      captchaToken: ref(null),
      resetCaptcha: (): void => {},
      waitForScriptLoad: async (): Promise<void> => {},
      renderWidget: (): void => {},
    };
  }

  // Load script on first use
  if (!scriptLoaded && !loadingPromise) {
    loadingPromise = loadPrivateCaptchaScript();
  }

  const waitForScriptLoad = async (): Promise<void> => {
    if (scriptLoaded) {
      return;
    }
    // A previous attempt may have failed (loadingPromise is cleared on error):
    // start a fresh one so retries can recover without a full page reload.
    const pending = loadingPromise ?? loadPrivateCaptchaScript();
    loadingPromise = pending;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<void>((_, reject) => {
      timerId = setTimeout(() => {
        // A stalled tag (connected, body never arrives) fires neither onload
        // nor onerror: drop it and clear the in-flight state so Try Again
        // starts a fresh attempt instead of racing the same hung promise.
        pendingScript?.remove();
        pendingScript = null;
        loadingPromise = null;
        const appError = new AppError('Private Captcha script load timeout', ErrorCodesDto.CAPTCHA_SCRIPT_TIMEOUT);
        sentryService.captureException(appError, {captchaPhase: 'script-timeout'});
        reject(appError);
      }, 30000);
    });
    try {
      await Promise.race([pending, timeout]);
    } finally {
      if (timerId !== null) {
        clearTimeout(timerId);
      }
    }
  };

  const resetCaptcha = (): void => {
    // Reset state - component will handle widget re-render
    isWidgetReady.value = false;
    isCaptchaLoading.value = false;
    isCaptchaSolved.value = false;
    captchaToken.value = null;
  };

  const renderWidget = (element: HTMLElement, options: {
    sitekey: string;
    theme?: string;
    lang?: string;
    displayMode?: string;
    startMode?: string;
    styles?: string;
  }): void => {
    if (!window.privateCaptcha?.render) {
      logger.error('Private Captcha render() not available')();
      sentryService.captureException(
        new AppError('Private Captcha render() not available', ErrorCodesDto.CAPTCHA_ERROR),
        {captchaPhase: 'render-unavailable'},
      );
      return;
    }

    // Tear down listeners from any prior render on this same element.
    widgetAbortControllers.get(element)?.abort();
    const controller = new AbortController();
    widgetAbortControllers.set(element, controller);
    const {signal} = controller;

    element.addEventListener('privatecaptcha:init', () => {
      isWidgetReady.value = true;
    }, {signal});

    element.addEventListener('privatecaptcha:start', () => {
      isCaptchaLoading.value = true;
    }, {signal});

    element.addEventListener('privatecaptcha:finish', ((event: CustomEvent<{widget: {solution(): string}}>) => {
      const widget = event.detail?.widget;
      const token = widget?.solution ? String(widget.solution()) : null;
      captchaToken.value = token;
      isCaptchaSolved.value = true;
      isCaptchaLoading.value = false;
    }) as EventListener, {signal});

    element.addEventListener('privatecaptcha:error', ((event: CustomEvent) => {
      logger.error('Private Captcha error:', event.detail)();
      sentryService.captureException(
        new AppError('Private Captcha widget error', ErrorCodesDto.CAPTCHA_ERROR),
        {captchaPhase: 'widget', detail: event.detail},
      );
      isCaptchaSolved.value = false;
      captchaToken.value = null;
      isCaptchaLoading.value = false;
    }) as EventListener, {signal});

    // Render the widget
    window.privateCaptcha.render(element, {
      sitekey: options.sitekey,
      theme: options.theme,
      lang: options.lang,
      displayMode: options.displayMode,
      startMode: options.startMode,
      styles: options.styles,
    });
  };

  return {
    isWidgetReady,
    isCaptchaLoading,
    isCaptchaSolved,
    captchaToken,
    resetCaptcha,
    waitForScriptLoad,
    renderWidget,
  };
}
